import type { ActionItem, Language, MeetingSummary } from "../types";
import { createLocalSummary } from "./ai";
import { createId } from "./id";

export type TranslationPair = "en-zh" | "zh-en";

export interface MockSearchResult {
  id: string;
  title: string;
  source: string;
  snippet: string;
  aiSummary: string;
}

function dateAfter(days: number): string {
  const date = new Date();
  date.setHours(12, 0, 0, 0);
  date.setDate(date.getDate() + days);
  return date.toISOString().slice(0, 10);
}

export function smartExtract(
  transcript: string,
  language: Language,
): ActionItem[] {
  const detected = createLocalSummary(transcript, language).actionItems;
  if (detected.length > 0) {
    return detected.slice(0, 4).map((item, index) => ({
      ...item,
      dueDate: dateAfter(index + 1),
    }));
  }
  const english = [
    ["Prepare the revised project proposal", "Alex", dateAfter(1)],
    ["Confirm the launch checklist with the team", "Jordan", dateAfter(3)],
    ["Share the final meeting notes", "Me", dateAfter(5)],
  ];
  const chinese = [
    ["整理并更新项目方案", "小林", dateAfter(1)],
    ["与团队确认上线检查清单", "张晨", dateAfter(3)],
    ["发送最终会议纪要", "我", dateAfter(5)],
  ];
  return (language === "zh-CN" ? chinese : english).map(
    ([task, owner, dueDate]) => ({
      id: createId(),
      task: task!,
      owner: owner!,
      dueDate: dueDate!,
      done: false,
    }),
  );
}

export function createMockMeetingSummary(
  transcript: string,
  language: Language,
  extractedItems: ActionItem[],
): MeetingSummary {
  const local = createLocalSummary(transcript, language);
  return {
    executiveSummary:
      local.executiveSummary ||
      (language === "zh-CN"
        ? "团队对当前进展进行了同步，并明确了下一阶段的重点任务与负责人。"
        : "The team aligned on current progress and clarified the priorities and owners for the next stage."),
    decisions:
      local.decisions.length > 0
        ? local.decisions
        : [
            language === "zh-CN"
              ? "采用当前方案进入下一轮验证。"
              : "Proceed with the current approach for the next validation round.",
          ],
    actionItems:
      extractedItems.length > 0
        ? extractedItems
        : smartExtract(transcript, language),
    risks:
      local.risks.length > 0
        ? local.risks
        : [
            language === "zh-CN"
              ? "时间安排仍需根据反馈进行调整。"
              : "Timing may need adjustment as feedback arrives.",
          ],
  };
}

function preview(input: string, length = 120): string {
  const normalized = input.replace(/\s+/g, " ").trim();
  return normalized.length > length
    ? `${normalized.slice(0, length).trim()}…`
    : normalized;
}

export function runMockSkill(
  skillId: string,
  input: string,
  language: Language,
): string {
  const sample = preview(input);
  if (language === "zh-CN") {
    const results: Record<string, string> = {
      summarize: `模拟摘要\n\n• 核心主题：${sample}\n• 当前状态：主要信息已归纳为可执行要点。\n• 建议下一步：确认负责人、截止日期和交付标准。`,
      polish: `润色后的文档\n\n${sample}\n\n我们建议以清晰、专业且面向行动的方式推进上述事项，并在下一次同步前确认关键交付成果。`,
      actions: `行动清单\n\n☐ 梳理输入内容中的关键目标 — 负责人：我\n☐ 与相关成员确认交付范围 — 负责人：项目团队\n☐ 在本周内发送最终版本 — 负责人：我`,
      report: `本周工作报告\n\n已完成\n• 完成核心事项梳理：${sample}\n\n下周计划\n• 验证主要交付成果\n• 收集团队反馈并更新计划\n\n风险与支持\n• 需要尽快确认资源和时间安排。`,
      translate: `模拟译文\n\nThe supplied business text has been translated into clear, professional English.\n\nSource preview: ${sample}`,
      agenda: `会议议程\n\n1. 目标与背景回顾（5 分钟）\n2. 重点事项讨论：${sample}（15 分钟）\n3. 决策与行动项确认（10 分钟）\n4. 负责人和截止日期确认（5 分钟）`,
    };
    return results[skillId] ?? results.summarize!;
  }
  const results: Record<string, string> = {
    summarize: `Mock summary\n\n• Core topic: ${sample}\n• Current state: The key information has been organized into actionable points.\n• Recommended next step: Confirm owners, deadlines, and success criteria.`,
    polish: `Polished document\n\n${sample}\n\nWe recommend moving forward with a clear, professional, and action-oriented approach, with key deliverables confirmed before the next review.`,
    actions: `Action checklist\n\n☐ Clarify the primary goal in the source text — Owner: Me\n☐ Confirm delivery scope with stakeholders — Owner: Project team\n☐ Share the final version this week — Owner: Me`,
    report: `Weekly report\n\nCompleted\n• Organized the core update: ${sample}\n\nNext week\n• Validate the primary deliverables\n• Collect team feedback and update the plan\n\nRisks and support\n• Resourcing and timing still need confirmation.`,
    translate: `模拟译文\n\n所提供的商务文本已转换为清晰、专业的中文。\n\n原文摘要：${sample}`,
    agenda: `Meeting agenda\n\n1. Goals and context (5 min)\n2. Key discussion: ${sample} (15 min)\n3. Decisions and action items (10 min)\n4. Confirm owners and deadlines (5 min)`,
  };
  return results[skillId] ?? results.summarize!;
}

export function mockTranslate(input: string, pair: TranslationPair): string {
  const sample = preview(input, 180);
  return pair === "en-zh"
    ? `【模拟译文】\n\n该内容已被转换为清晰、自然且适合商务场景的中文。\n\n原文要点：${sample}`
    : `[Mock translation]\n\nThis content has been translated into clear, natural English suitable for a professional setting.\n\nSource highlights: ${sample}`;
}

export function mockSearch(
  query: string,
  language: Language,
): MockSearchResult[] {
  const q = query.trim();
  if (language === "zh-CN") {
    return [
      {
        id: "search-1",
        title: `${q}：快速背景说明`,
        source: "模拟行业简报",
        snippet: `这份简报汇总了与“${q}”相关的主要背景、近期变化和常用实践。`,
        aiSummary: `AI 模拟摘要：${q} 的核心趋势是团队正在从试验阶段转向可衡量、可复用的工作流程。`,
      },
      {
        id: "search-2",
        title: `${q} 的五个实施要点`,
        source: "模拟知识库",
        snippet:
          "实施重点包括明确目标、分配负责人、控制风险、设置指标并定期复盘。",
        aiSummary:
          "AI 模拟摘要：先从小范围场景开始，并为每项成果设置明确负责人。",
      },
      {
        id: "search-3",
        title: `团队如何评估 ${q}`,
        source: "模拟研究笔记",
        snippet: "高效团队会同时评估效率、质量、采用率和长期维护成本。",
        aiSummary:
          "AI 模拟摘要：不要只关注速度，还应跟踪结果质量与团队采用情况。",
      },
    ];
  }
  return [
    {
      id: "search-1",
      title: `${q}: a quick background guide`,
      source: "Mock industry brief",
      snippet: `This brief collects the main context, recent shifts, and common practices related to “${q}.”`,
      aiSummary: `Mock AI summary: The central ${q} trend is a move from experiments to measurable, repeatable workflows.`,
    },
    {
      id: "search-2",
      title: `Five implementation principles for ${q}`,
      source: "Mock knowledge base",
      snippet:
        "The main principles are clear goals, named owners, controlled risk, useful metrics, and regular review.",
      aiSummary:
        "Mock AI summary: Start with a narrow use case and assign a clear owner to every outcome.",
    },
    {
      id: "search-3",
      title: `How teams evaluate ${q}`,
      source: "Mock research notes",
      snippet:
        "Effective teams evaluate efficiency, quality, adoption, and long-term maintenance cost together.",
      aiSummary:
        "Mock AI summary: Do not measure speed alone; track output quality and team adoption as well.",
    },
  ];
}
